import { useEffect, useMemo, useState } from 'react';
import { OverlayView, OverlayViewF } from '@react-google-maps/api';
import MapCanvas from '../../../../components/Map';
import Polygon from '../../../../components/Map/polygon';
import { orDash } from '../../../../utils/schema-parse';
import type { ParsedKml } from '../../../../utils/kml';
import type { RelatorioVisitaPonto } from '../../schemas/payload.schema';
import iconeDoMarcador from './marcador-icone';

interface GlebaMapaProps {
  parsed: ParsedKml;
  pontos: RelatorioVisitaPonto[];
  altura?: number;
}

const OPCOES_POLIGONO: google.maps.PolygonOptions = {
  strokeColor: '#28a54c',
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: '#28a54c',
  fillOpacity: 0.15,
};

interface PontoComCoordenada extends RelatorioVisitaPonto {
  latitude: number;
  longitude: number;
}

const temCoordenada = (
  ponto: RelatorioVisitaPonto,
): ponto is PontoComCoordenada =>
  ponto.latitude != null &&
  ponto.longitude != null &&
  Number.isFinite(ponto.latitude) &&
  Number.isFinite(ponto.longitude) &&
  (ponto.latitude !== 0 || ponto.longitude !== 0);

const deslocamentoMarcador = (largura: number, altura: number) => ({
  x: -largura / 2,
  y: -altura,
});

const GlebaMapa = ({ parsed, pontos, altura = 360 }: GlebaMapaProps) => {
  const [mapa, setMapa] = useState<google.maps.Map>();

  const { polygons, points } = parsed;

  const pontosComCoordenada = useMemo(
    () => pontos.filter(temCoordenada),
    [pontos],
  );

  useEffect(() => {
    if (!mapa) return;

    const limites = new google.maps.LatLngBounds();

    const coordenadas = [
      ...points,
      ...pontosComCoordenada.map((ponto) => ({
        lat: ponto.latitude,
        lng: ponto.longitude,
      })),
    ];

    coordenadas.forEach((coordenada) => limites.extend(coordenada));

    if (limites.isEmpty()) return;

    mapa.fitBounds(limites);
  }, [mapa, points, pontosComCoordenada]);

  if (polygons.length === 0 && pontosComCoordenada.length === 0) return null;

  return (
    <MapCanvas
      mapContainerStyle={{ width: '100%', height: `${altura}px` }}
      mapTypeId="satellite"
      onLoad={setMapa}
    >
      {polygons.map((caminhos) => (
        <Polygon
          key={`poly-${caminhos[0][0].lat},${caminhos[0][0].lng}-${caminhos[0].length}`}
          options={OPCOES_POLIGONO}
          paths={caminhos}
        />
      ))}

      {pontosComCoordenada.map((ponto) => {
        const icone = iconeDoMarcador(ponto.marcador);

        if (!icone) return null;

        return (
          <OverlayViewF
            key={ponto.ordem}
            getPixelPositionOffset={deslocamentoMarcador}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            position={{ lat: ponto.latitude, lng: ponto.longitude }}
          >
            <img
              alt={orDash(ponto.tipo)}
              className="rv-ponto-marker-icone"
              src={icone}
              title={orDash(ponto.tipo)}
            />
          </OverlayViewF>
        );
      })}
    </MapCanvas>
  );
};

export default GlebaMapa;
